import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, Chip, CircularProgress } from '@mui/material';
import { teamService } from 'services/teamService';
import { StyledContainer } from './styles';

interface Team {
  id: number;
  name: string;
  colorTeam?: string;
}

export interface TeamMultiSelectAutocompleteProps {
  /** Valores selecionados (IDs) */
  value?: number[];
  /** Função chamada quando os valores mudam */
  onChange: (value: number[]) => void;
  /** Label do campo */
  label?: string;
  /** Se o componente está desabilitado */
  disabled?: boolean;
  /** Placeholder quando nenhum item está selecionado */
  placeholder?: string;
  /** Loading externo */
  loading?: boolean;
}

export const TeamMultiSelectAutocomplete = ({
  value = [],
  onChange,
  label = "Times",
  disabled = false,
  placeholder = "Selecione os times",
  loading: externalLoading = false
}: TeamMultiSelectAutocompleteProps) => {
  const [options, setOptions] = useState<Team[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<Team[]>([]);

  // Busca os times da API
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        setInternalLoading(true);
        const response = await teamService.getTeam({
          pageSize: 1000
        });

        if (response && response?.results) {
          setOptions(response?.results);
        }
      } catch (error) {
        console.error('Erro ao buscar times:', error);
      } finally {
        setInternalLoading(false);
      }
    };

    fetchTeams();
  }, []);

  // Atualiza os valores selecionados quando o prop value muda
  useEffect(() => {
    if (value && value.length > 0 && options.length > 0) {
      const selected = options.filter(option => value.includes(option.id));
      setSelectedOptions(selected);
    } else {
      setSelectedOptions([]);
    }
  }, [value, options]);

  const handleChange = (_: any, newValue: Team[]) => {
    setSelectedOptions(newValue);
    const ids = newValue.map(option => option.id);
    onChange(ids);
  };

  const getOptionLabel = (option: Team) => {
    return option.name;
  };

  const isLoading = internalLoading || externalLoading;

  return (
    <StyledContainer>
      <Autocomplete
        multiple
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        getOptionLabel={getOptionLabel}
        disabled={disabled || isLoading}
        loading={isLoading}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              label={option.name}
              size="small"
              sx={{
                backgroundColor: '#89BD23',
                color: 'white',
                '& .MuiChip-deleteIcon': {
                  color: 'white',
                  '&:hover': {
                    color: '#f0f0f0'
                  }
                }
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={selectedOptions.length === 0 ? placeholder : ''}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        filterSelectedOptions
        sx={{
          '& .MuiOutlinedInput-root': {
            padding: '8px 12px',
          }
        }}
      />
    </StyledContainer>
  );
};

